// Getting the app installed, which on Android is what puts it in WhatsApp's
// share sheet.
//
// The share target in the manifest only exists once the app is installed, so
// "Export chat → OkaZip" is not a feature we can ship — it is a feature the
// person has to accept. Which makes the install prompt worth asking for, and
// worth asking for with that reason rather than "install our app".
//
// Chrome decides whether an app is installable and says so by firing
// `beforeinstallprompt`, which it does once, early, and which must be caught
// before React exists — hence a module with a listener at import time rather
// than a hook. Everywhere that cannot install (every iPhone, an app already
// installed, a browser that has had enough) simply never fires it, so there
// is nothing to feature-detect and nothing to hide.

/** The event Chrome hands over. Not in lib.dom. */
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const SAID_NO = 'wa.install.no';

let waiting: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const tell = () => listeners.forEach((f) => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Kept rather than allowed to show Chrome's own bar, so the offer can be
    // made where it means something instead of over the page.
    e.preventDefault();
    waiting = e as InstallPrompt;
    tell();
  });
  // Nothing more to ask for, and the answer to "is it installed" that does
  // not depend on the display mode of the tab we happen to be in.
  window.addEventListener('appinstalled', () => { waiting = null; tell(); });
}

const saidNo = () => {
  try { return localStorage.getItem(SAID_NO) === '1'; } catch { return false; }
};

/** Whether there is an offer to make. */
export const canInstall = () => waiting !== null && !saidNo();

/** @returns whether they went through with it. */
export async function install(): Promise<boolean> {
  const e = waiting;
  if (!e) return false;
  // One use each. Chrome refuses a second `prompt()` on the same event, so
  // the offer goes away whichever way they answer and comes back on a later
  // visit if Chrome fires again.
  waiting = null;
  tell();
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'dismissed') return false;
  return true;
}

/** Not now, and not on the next load either. */
export function dismiss() {
  try { localStorage.setItem(SAID_NO, '1'); } catch { /* private mode */ }
  waiting = null;
  tell();
}

export function onInstallable(f: () => void): () => void {
  listeners.add(f);
  return () => { listeners.delete(f); };
}
